import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import ReceivingInspectionPrintView, {
  type ReceivingInspectionPrintRecord,
} from '@/components/quality/ReceivingInspectionPrintView';
import { qualityService } from '@/services/quality.service';
import { useSystemSettings } from '@/hooks/useSettings';
import { approvalService } from '@/services/approval.service';
import { ENTITY_TYPE_RECEIVING_INSPECTION } from '@/constants/approval-entity-types';

const ReceivingInspectionPrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: settings = [] } = useSystemSettings();
  const didTriggerPrint = useRef(false);

  const { data: record, isLoading, isError, error } = useQuery({
    queryKey: ['receiving-inspection-print', id],
    queryFn: async () => {
      if (!id) throw new Error('Missing id');
      return (await qualityService.getInspectionById(id)) as ReceivingInspectionPrintRecord;
    },
    enabled: !!id,
  });

  const { data: approvalSigners = [], isFetched: signersFetched } = useQuery({
    queryKey: ['receiving-inspection-print-signers', id],
    queryFn: async () => {
      if (!id) return [];
      try {
        return await approvalService.getPrintSigners(ENTITY_TYPE_RECEIVING_INSPECTION, id);
      } catch {
        return [];
      }
    },
    enabled: !!id,
  });

  const companySettings = useMemo(
    () => settings.filter((setting) => setting.category === 'company'),
    [settings]
  );

  const companyInfo = useMemo(() => {
    const getSetting = (key: string) =>
      companySettings.find((setting) => setting.setting_key === key)?.setting_value?.trim() ?? '';
    const name = getSetting('company_name') || 'Company Name';
    return {
      name,
      legalName: getSetting('company_legal_name') || name,
      address: [
        getSetting('company_address'),
        getSetting('company_city'),
        getSetting('company_country'),
      ]
        .filter(Boolean)
        .join(', '),
      logo: getSetting('company_logo'),
    };
  }, [companySettings]);

  useEffect(() => {
    if (!record || !signersFetched || didTriggerPrint.current) return;
    didTriggerPrint.current = true;
    const t = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(t);
  }, [record, signersFetched]);

  if (isLoading) return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  if (isError || !record) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ margin: 24 }}
        message="Unable to load Receiving Inspection for print"
        description={(error as Error)?.message}
      />
    );
  }

  return (
    <PrintPreviewChrome
      title={`Receiving Inspection ${record.inspection_number}`}
      onBack={() => navigate(`/scm/procurement/receiving-inspections/${id}`)}
    >
      <ReceivingInspectionPrintView
        record={record}
        companyInfo={companyInfo}
        approvalSigners={approvalSigners}
      />
    </PrintPreviewChrome>
  );
};

export default ReceivingInspectionPrintPage;
